import { useId, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ExternalLink, MapPin, Plus, Repeat } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { BuscaRemota, CampoControlado, linhasExtras, Rotulo, Selecao, useCamposPersonalizados, valorExtra, EntradaSensivel, type DefCampo } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { EdicaoRapida } from '../../componentes/edicao-rapida';
import { getTodos, mensagemDeErro, patch } from '../../nucleo/api';
import { EXTRAS_FORA_DO_CRM } from '../../nucleo/extras-crm';
import { nomeParceiro, textoLivre } from '../../nucleo/formato';
import { modulosWeb } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import type { Oportunidade } from './tipos';
import { rotuloRegime } from './crm-regime';

/**
 * Ajustes do CRM (27/10, F1): o que o lado esquerdo da ficha da negociação ganhou para ficar como o RD. Os campos
 * personalizados da negociação (edição no lugar), a seção Empresa com segmento, URL e endereço (editáveis com o Cadastros),
 * "Abrir página da Empresa" e trocar a empresa (existente ou nova), e as "Informações adicionais" de cada contato (cargo,
 * campos personalizados e base legal). O formulário de contato e empresa é o do Cadastros (`formularioParceiro`), sem sair.
 */

export const CAMINHO_EMPRESA_CRM = (id: string) => `/crm/empresas/${id}`;
export const CAMINHO_CONTATO_CRM = (id: string) => `/crm/contatos/${id}`;

export interface EmpresaFicha {
  id: string;
  nome: string;
  tipo: 'pessoa' | 'empresa';
  setor_id: string | null;
  setor_nome: string | null;
  site: string | null;
  endereco: string | null;
  /** crm6 item 11: regime tributário em vigor da empresa (Cadastros). */
  regime?: string | null;
}

export interface ContatoFicha {
  parceiro_id: string;
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  principal: boolean;
  extras: Record<string, unknown> | null;
  base_legal: string | null;
}

/** O formulário de contato ou empresa do Cadastros, com o módulo instalado (null sem ele). */
export function useFormularioParceiro() {
  const s = useSessao();
  const m = modulosWeb.find((x) => x.formularioParceiro && x.slug === 'cadastros');
  return m && s.instalado('cadastros') ? m.formularioParceiro! : null;
}

const lerExtra = (extras: Record<string, unknown> | null | undefined, chave: string) => (extras ? extras[chave] : undefined);

/** Desenho da linha só de leitura da ficha, o mesmo da linha da edição rápida (`EdicaoRapida`). */
const CLASSE_LINHA = 'flex min-h-11 items-center justify-between gap-3 border-b border-borda px-2 py-2 text-sm last:border-0';

/**
 * Ajustes do CRM (27/10, F1): os campos personalizados da negociação, cada um editável no lugar (grava só ele em `extras`).
 * Lista de 30/10 (B), item 7.1: pela edição rápida do perfil da empresa (`EdicaoRapida`).
 */
export function CamposExtrasNegociacao({ o, podeEditar, aoSalvar }: { o: Oportunidade; podeEditar: boolean; aoSalvar: () => Promise<void> }) {
  const defs = useCamposPersonalizados('pipeline.oportunidade');
  const visiveis = defs.filter((d) => !d.visivel || d.visivel(o as unknown as Record<string, unknown>));
  return (
    <>
      {visiveis.map((d) => (
        <CampoExtra key={d.nome} o={o} d={d as DefCampo} podeEditar={podeEditar} aoSalvar={aoSalvar} />
      ))}
    </>
  );
}

function CampoExtra({ o, d, podeEditar, aoSalvar }: { o: Oportunidade; d: DefCampo; podeEditar: boolean; aoSalvar: () => Promise<void> }) {
  const chave = d.nome.slice(7);
  // O valor cru só entra no campo de edição; a leitura passa por `valorExtra` (modo discreto).
  const atual = lerExtra(o.extras, chave);
  const [valor, setValor] = useState<unknown>(null);
  return (
    <EdicaoRapida
      rotulo={d.rotulo}
      exibir={valorExtra(d, atual, o.parceiro_nome)}
      pode={podeEditar}
      aoAbrir={() => setValor(atual ?? (d.tipo === 'booleano' ? false : ''))}
      aoSalvar={async () => {
        await patch(`/pipeline/oportunidades/${o.id}`, { extras: { [chave]: valor === '' ? null : valor } });
        await aoSalvar();
      }}
      data-campo-ficha={`extras.${chave}`}
    >
      {() => <CampoControlado def={d} valor={valor} aoMudar={setValor} />}
    </EdicaoRapida>
  );
}

/**
 * Ajustes do CRM (27/10, F1): a seção Empresa do RD. Nome com o link para a página da empresa no CRM, segmento, URL e endereço
 * (segmento e URL editáveis com `cadastros.editar`), "Abrir página da Empresa" e trocar a empresa da negociação por outra do
 * cadastro ou por uma nova. Sem a leitura do Cadastros, só o nome (como antes).
 */
export function EmpresaNegociacao({ o, empresa, podeEditar, aoMudar, comAdicionais = false }: { o: Oportunidade; empresa: EmpresaFicha | null; podeEditar: boolean; aoMudar: () => Promise<void>; /** Lista de 03/11, item 3: com empresas adicionais, esta é marcada como a principal. */ comAdicionais?: boolean }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const id = useId();
  const Formulario = useFormularioParceiro();
  const editaCadastro = s.instalado('cadastros') && s.pode('cadastros.editar') && !!empresa;
  const [setor, setSetor] = useState('');
  const [site, setSite] = useState('');
  const [abriuSetor, setAbriuSetor] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [nova, setNova] = useState(false);
  const setores = useQuery({ queryKey: ['cadastros', 'setores'], queryFn: () => getTodos<{ id: string; nome: string }>('/cadastros/setores'), enabled: abriuSetor });
  const salvar = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: aoMudar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const trocar = (parceiro: string) =>
    salvar.mutate(async () => {
      await patch(`/pipeline/oportunidades/${o.id}`, { parceiro_id: parceiro });
      setTrocando(false);
      setEscolhida(null);
    });
  const nome = empresa?.nome ?? o.parceiro_nome;
  return (
    <div className="flex flex-col py-1" data-empresa-negociacao="">
      <div className={CLASSE_LINHA}>
        <span className="text-tinta-3">{t('crm.ficha.campos.nome')}</span>
        <span className="flex min-w-0 items-center gap-1">
          {nome === null || nome === undefined ? (
            <span className="text-tinta-2">{nomeParceiro(nome)}</span>
          ) : (
            <Link to={CAMINHO_EMPRESA_CRM(o.parceiro_id)} className="min-w-0 truncate font-semibold text-acento-forte hover:underline" title={nomeParceiro(nome)}>
              {nomeParceiro(nome)}
            </Link>
          )}
          {comAdicionais && <Selo cor="azul">{t('crm.ficha.principal')}</Selo>}
          {podeEditar && s.instalado('cadastros') && s.pode('cadastros.ver') && (
            <button type="button" aria-label={t('crm.ficha2.trocarEmpresa')} title={t('crm.ficha2.trocarEmpresa')} aria-expanded={trocando} className="rounded p-1 text-tinta-3 hover:bg-sup-3 hover:text-tinta-1" onClick={() => setTrocando((x) => !x)} data-trocar-empresa="">
              <Repeat className="size-3.5" aria-hidden />
            </button>
          )}
        </span>
      </div>
      {trocando && (
        <div className="flex flex-col gap-2 rounded-lg border border-borda bg-sup-2 p-2">
          <Rotulo id={`${id}-empresa`} rotulo={t('crm.ficha2.empresaExistente')}>
            <BuscaRemota id={`${id}-empresa`} def={{ caminho: '/cadastros/parceiros', params: { tipo: 'empresa' }, rotulo: (i) => nomeParceiro(String(i.nome)) }} valor={escolhida} aoMudar={(v) => setEscolhida(v)} />
          </Rotulo>
          {Formulario && s.pode('cadastros.criar') && (
            <button type="button" className="flex w-fit items-center gap-1 text-sm font-medium text-acento-forte hover:underline" onClick={() => setNova(true)} data-criar-empresa="">
              <Plus className="size-4" aria-hidden />
              {t('crm.ficha2.criarEmpresa')}
            </button>
          )}
          <div className="flex justify-end gap-2">
            <Botao tamanho="sm" variante="fantasma" onClick={() => setTrocando(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao tamanho="sm" variante="primario" disabled={!escolhida || escolhida === o.parceiro_id} carregando={salvar.isPending} onClick={() => escolhida && trocar(escolhida)} data-trocar-empresa-confirmar="">
              {tn('comum.salvar')}
            </Botao>
          </div>
        </div>
      )}
      {empresa && (
        <>
          {/* Lista de 30/10 (B), item 7.1: segmento e URL da empresa pela edição rápida, como no perfil da empresa. */}
          <EdicaoRapida
            rotulo={t('crm.ficha2.segmento')}
            exibir={empresa.setor_nome ? textoLivre(empresa.setor_nome) : ''}
            pode={podeEditar && editaCadastro}
            aoAbrir={() => {
              setSetor(empresa.setor_id ?? '');
              setAbriuSetor(true);
            }}
            aoSalvar={async () => {
              await patch(`/cadastros/parceiros/${empresa.id}`, { setor_id: setor || null });
              await aoMudar();
            }}
            data-campo-ficha={t('crm.ficha2.segmento')}
          >
            {({ id: idCampo, erro }) => (
              <Rotulo id={idCampo} rotulo={t('crm.ficha2.segmento')} erro={erro || undefined}>
                <Selecao id={idCampo} value={setor} aria-invalid={!!erro} vazio={tn('comum.selecione')} opcoes={[...(empresa.setor_id && !(setores.data?.itens ?? []).some((x) => x.id === empresa.setor_id) ? [{ valor: empresa.setor_id, rotulo: textoLivre(empresa.setor_nome ?? '') }] : []), ...(setores.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome }))]} onChange={(e) => setSetor(e.target.value)} />
              </Rotulo>
            )}
          </EdicaoRapida>
          <EdicaoRapida
            rotulo={t('crm.ficha2.url')}
            exibir={empresa.site ? textoLivre(empresa.site) : ''}
            pode={podeEditar && editaCadastro}
            aoAbrir={() => setSite(empresa.site ?? '')}
            aoSalvar={async () => {
              await patch(`/cadastros/parceiros/${empresa.id}`, { site: site.trim() || null });
              await aoMudar();
            }}
            data-campo-ficha={t('crm.ficha2.url')}
          >
            {({ id: idCampo, erro }) => (
              <Rotulo id={idCampo} rotulo={t('crm.ficha2.url')} erro={erro || undefined}>
                <EntradaSensivel id={idCampo} mascara={null} type="url" maxLength={300} placeholder="https://" value={site} aria-invalid={!!erro} onChange={(e) => setSite(e.target.value)} />
              </Rotulo>
            )}
          </EdicaoRapida>
          <div className={CLASSE_LINHA} data-campo-ficha={t('crm.ficha2.endereco')}>
            <span className="text-tinta-3">{t('crm.ficha2.endereco')}</span>
            <span className="flex min-w-0 items-center gap-1">
              <span className="min-w-0 truncate text-right" title={empresa.endereco ? textoLivre(empresa.endereco) : undefined}>
                {empresa.endereco ? textoLivre(empresa.endereco) : ''}
              </span>
              {empresa.endereco && <MapPin className="size-3.5 shrink-0 text-tinta-3" aria-hidden />}
            </span>
          </div>
          <div className={CLASSE_LINHA} data-campo-ficha={t('cadastros:campos.regime')} data-regime-empresa-ficha={empresa.regime ?? ''}>
            <span className="text-tinta-3">{t('cadastros:campos.regime')}</span>
            <span className="min-w-0 truncate text-right" title={rotuloRegime(empresa.regime) || undefined}>
              {rotuloRegime(empresa.regime)}
            </span>
          </div>
        </>
      )}
      <Link to={CAMINHO_EMPRESA_CRM(o.parceiro_id)} className="mt-1 flex w-fit items-center gap-1 text-sm font-medium text-acento-forte hover:underline" data-abrir-empresa="">
        {t('crm.ficha2.abrirEmpresa')}
        <ExternalLink className="size-3.5" aria-hidden />
      </Link>
      {Formulario && nova && <Formulario tipo="empresa" aberto={nova} aoFechar={() => setNova(false)} aoSalvar={(nid) => trocar(nid)} />}
    </div>
  );
}

const BASES_LEGAIS = ['nao_sabe', 'consentimento', 'legitimo_interesse', 'execucao_contrato', 'obrigacao_legal'] as const;

/** Ajustes do CRM (27/10, F1): "Informações adicionais" do contato, recolhível: cargo, campos personalizados e base legal. */
export function InfoAdicionalContato({ c }: { c: ContatoFicha }) {
  const { t } = useTranslation('pipeline');
  const [aberto, setAberto] = useState(false);
  const id = useId();
  const defs = useCamposPersonalizados(aberto ? 'cadastros.parceiro' : undefined);
  // rl0211-1 SPEC-B1: o campo que virou tipo de telefone (WhatsApp) não aparece aqui, como nas fichas do contato e da empresa.
  const defsCrm = defs.filter((d) => !EXTRAS_FORA_DO_CRM.some((k) => d.nome === `extras.${k}`));
  const extras = linhasExtras(defsCrm, Object.fromEntries(Object.entries(c.extras ?? {}).filter(([k]) => !EXTRAS_FORA_DO_CRM.includes(k))), c.nome);
  const base = BASES_LEGAIS.includes(c.base_legal as never) ? c.base_legal! : 'nao_sabe';
  return (
    <div className="mt-1">
      <button type="button" aria-expanded={aberto} aria-controls={id} className="flex w-full items-center justify-between gap-2 rounded px-1 py-1 text-xs font-medium text-tinta-2 hover:bg-sup-3" onClick={() => setAberto((x) => !x)} data-info-adicional="">
        {t('crm.ficha2.infoAdicional')}
        <ChevronDown className={`size-3.5 transition-transform ${aberto ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {aberto && (
        <dl id={id} className="flex flex-col gap-1 px-1 pt-1 text-xs">
          <div className="flex justify-between gap-2">
            <dt className="text-tinta-3">{t('crm.ficha2.cargo')}</dt>
            <dd className="min-w-0 truncate text-right font-medium" title={c.cargo ? textoLivre(c.cargo) : undefined}>
              {c.cargo ? textoLivre(c.cargo) : ''}
            </dd>
          </div>
          {extras.map((l) => (
            <div key={l.chave} className="flex justify-between gap-2">
              <dt className="text-tinta-3">{l.rotulo}</dt>
              <dd className="min-w-0 truncate text-right font-medium" title={l.valor}>
                {l.valor}
              </dd>
            </div>
          ))}
          <div className="flex justify-between gap-2">
            <dt className="text-tinta-3">{t('crm.ficha2.baseLegal')}</dt>
            <dd className="min-w-0 text-right font-medium">{t(`crm.ficha2.basesLegais.${base}`)}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}
